import { SyncPushSchema, type Evidence, type Project, type Snag, type User } from "@revival/contracts";
import { api, ApiError, type Bootstrap } from "./api.js";
import { queuedWork } from './snag-queue.js';
import { disconnected, isNetworkFailure, NetworkError } from './offline-cache.js';

type LegacyProject = {
  id: string;
  name: string;
  phase: string;
  address: string;
  jobReference: string;
  totalSnags: number;
  openSnags: number;
  reviewSnags: number;
  closedSnags: number;
};

type LegacyContractor = {
  id: string;
  company: string;
  trade: string;
  contactName: string;
  telephone: string;
  email: string;
  fullSnagListReadOnly: boolean;
  accountCount: number;
  initials: string;
  accessCode: string;
};

type LegacyDrawing = {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
};

type LegacySnag = {
  id: string;
  reference: string;
  projectId: string;
  locationId?: string | null;
  title: string;
  description: string;
  area: string;
  trade: string;
  priority: string;
  contractorId: string | null;
  contractorCompany: string | null;
  contractorInitials: string | null;
  status: string;
  dueDate: string | null;
  completionNote: string;
  managerNote: string;
  rejectionNote: string;
  createdAt: string;
  updatedAt: string;
  version?: number;
  pendingSync?: boolean;
  commentAttention?: boolean;
  videoEvidenceIds?: string[];
  issuePhotoId: string | null;
  completionPhotoId: string | null;
  drawingId: string | null;
  drawingPage?: number | null;
  pinCoordinateSpace?: "image" | "legacy";
  drawingName: string | null;
  drawingContentType: string | null;
  markupX: number | null;
  markupY: number | null;
  markupLabel: string | null;
  readOnly: boolean;
  recordState: "live" | "archived" | "deleted";
  recordStateChangedAt: string | null;
};

export type LegacyAppData = {
  mode: "manager" | "contractor";
  currentContractor: LegacyContractor | null;
  project: LegacyProject;
  projects: LegacyProject[];
  contractors: LegacyContractor[];
  locations: Array<{ id: string; name: string }>;
  drawings: LegacyDrawing[];
  snags: LegacySnag[];
};

const ACTIVE_PROJECT_KEY = "site-audit-active-project";
const DEVICE_ID_KEY = "site-audit-web-device-id";
let mediaUrls = new Map<string, string>();
let mediaRequests = new Map<string, Promise<string>>();
let mediaGeneration = 0;
let cachedProjectUser = "";
let dataRequestGeneration = 0;
let projectSnapshots = new Map<string, Bootstrap>();
const snagProjectIds = new Map<string, string>();

export function isLocalDemo(): boolean {
  return import.meta.env.DEV && new URLSearchParams(window.location.search).has("demo");
}

let sessionDeviceId = crypto.randomUUID();
function deviceId(): string {
  try { const existing = localStorage.getItem(DEVICE_ID_KEY); if (existing) return existing; localStorage.setItem(DEVICE_ID_KEY, sessionDeviceId); } catch { /* Private browsing can deny storage; operation IDs remain stable. */ }
  return sessionDeviceId;
}

function initials(value: string): string {
  return value.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "CT";
}

function mappedStatus(value: Snag["status"]): string {
  return value === "ready_to_inspect" ? "ready_for_inspection" : value;
}

function serverStatus(value: string): Snag["status"] {
  if (value === "ready_for_inspection") return "ready_to_inspect";
  if (value === "returned") return "open";
  if (["open", "in_progress", "ready_to_inspect", "closed"].includes(value)) return value as Snag["status"];
  return "open";
}

function projectSummary(project: Project, data: Bootstrap): LegacyProject {
  const open = data.snags.filter((snag) => ["open", "in_progress"].includes(snag.status)).length;
  const review = data.snags.filter((snag) => snag.status === "ready_to_inspect").length;
  const closed = data.snags.filter((snag) => snag.status === "closed").length;
  return {
    id: project.id,
    name: project.name,
    phase: project.phase,
    address: project.address,
    jobReference: project.jobReference,
    totalSnags: data.snags.length,
    openSnags: open,
    reviewSnags: review,
    closedSnags: closed,
  };
}

function contractorRows(data: Bootstrap, user: User): LegacyContractor[] {
  const contractorCompanyId = data.currentContractorCompanyId ?? user.contractorCompanyId;
  const rows = data.contractorCompanies.map((item) => ({
    id: item.id,
    company: item.company,
    trade: item.trade,
    contactName: item.contactName,
    telephone: item.telephone,
    email: item.email,
    fullSnagListReadOnly: item.fullSnagListReadOnly,
    accountCount: item.accountCount,
    initials: initials(item.company),
    accessCode: item.accountCount === 0 ? "No accounts yet" : `${item.accountCount} active account${item.accountCount === 1 ? "" : "s"}`,
  }));
  if (user.role === "contractor" && contractorCompanyId && !rows.some((item) => item.id === contractorCompanyId)) {
    rows.push({ id: contractorCompanyId, company: user.name, trade: "Contractor", contactName: user.name, telephone: "", email: "", fullSnagListReadOnly: false, accountCount: 1, initials: initials(user.name), accessCode: "1 active account" });
  }
  return rows;
}

function evidenceId(evidence: Evidence[], snagId: string, kind: Evidence["kind"]): string | null {
  return [...evidence].reverse().find((item) => item.snagId === snagId && item.kind === kind)?.id ?? null;
}

function snagRows(data: Bootstrap, contractors: LegacyContractor[], user: User): LegacySnag[] {
  const contractorCompanyId = data.currentContractorCompanyId ?? user.contractorCompanyId;
  const locations = new Map(data.locations.map((item) => [item.id, item.name]));
  const companies = new Map(contractors.map((item) => [item.id, item]));
  const drawings = new Map(data.drawings.map((item) => [item.id, item]));
  return data.snags.map((snag) => {
    const contractor = snag.contractorCompanyId ? companies.get(snag.contractorCompanyId) : null;
    const drawing = snag.drawingId ? drawings.get(snag.drawingId) : null;
    return {
      id: snag.id,
      reference: snag.reference,
      projectId: snag.projectId,
      locationId: snag.locationId,
      title: snag.title,
      description: snag.description,
      area: snag.locationId ? locations.get(snag.locationId) ?? "Area not recorded" : "Area not recorded",
      trade: snag.trade || contractor?.trade || "General",
      priority: snag.priority,
      contractorId: snag.contractorCompanyId,
      contractorCompany: contractor?.company ?? null,
      contractorInitials: contractor?.initials ?? null,
      status: mappedStatus(snag.status),
      dueDate: snag.dueDate,
      completionNote: snag.completionNote,
      managerNote: snag.managerNote,
      rejectionNote: snag.status === "open" ? snag.managerNote : "",
      createdAt: snag.createdAt,
      updatedAt: snag.updatedAt,
      version: snag.version,
      commentAttention: data.commentAttention?.includes(snag.id) ?? false,
      videoEvidenceIds: data.evidence.filter((item) => item.snagId === snag.id && item.kind === "issue_video").map((item) => item.id),
      issuePhotoId: evidenceId(data.evidence, snag.id, "issue"),
      completionPhotoId: evidenceId(data.evidence, snag.id, "completion"),
      drawingId: snag.drawingId,
      drawingPage: snag.drawingPage,
      pinCoordinateSpace: snag.pinCoordinateSpace ?? "legacy",
      drawingName: drawing?.fileName ?? null,
      drawingContentType: drawing?.contentType ?? null,
      markupX: snag.markupX == null ? null : Math.round(snag.markupX * 10_000),
      markupY: snag.markupY == null ? null : Math.round(snag.markupY * 10_000),
      markupLabel: null,
      readOnly: user.role === "contractor" && snag.contractorCompanyId !== contractorCompanyId,
      recordState: snag.recordState,
      recordStateChangedAt: snag.recordStateChangedAt,
    };
  });
}

export function archivedProjectRows(data: Bootstrap, user: User): LegacyAppData {
  const contractors = contractorRows(data, user);
  return { mode: "manager", currentContractor: null, project: projectSummary(data.project, data), projects: [], contractors,
    locations: data.locations, drawings: data.drawings.map((drawing) => ({ id: drawing.id, fileName: drawing.fileName, contentType: drawing.contentType, size: drawing.sizeBytes })),
    snags: snagRows(data, contractors, user).map((snag) => ({ ...snag, readOnly: true })) };
}

function blankProject(): LegacyProject {
  return { id: "", name: "No project selected", phase: "", address: "", jobReference: "", totalSnags: 0, openSnags: 0, reviewSnags: 0, closedSnags: 0 };
}

export async function prepareAudit(projectId: string, drafts: Array<Record<string, any>>, locations?: Array<{ id: string; name: string }>) {
      const data = locations ? { locations } : await api.bootstrap(projectId);
      const operations = drafts.map((draft) => {
        const location = data.locations.find((item) => item.name.toLowerCase() === String(draft.area ?? "").toLowerCase());
        return {
          kind: "create_snag" as const,
          operationId: draft.clientId || crypto.randomUUID(),
          payload: {
            clientId: draft.clientId || crypto.randomUUID(),
            projectId,
            locationId: location?.id ?? null,
            contractorCompanyId: draft.contractorId ? String(draft.contractorId) : null,
            drawingId: draft.drawingId ? String(draft.drawingId) : null,
            drawingPage: draft.drawingId ? 1 : null,
            pinCoordinateSpace: draft.pinCoordinateSpace === "image" ? "image" as const : "legacy" as const,
            markupX: draft.markupX == null ? null : Number(draft.markupX) / 10_000,
            markupY: draft.markupY == null ? null : Number(draft.markupY) / 10_000,
            title: String(draft.title),
            description: String(draft.description ?? ""),
            trade: String(draft.trade || "General"),
            priority: ["low", "normal", "high", "urgent"].includes(String(draft.priority)) ? draft.priority : "normal",
            dueDate: draft.dueDate ? String(draft.dueDate) : null,
            capturedAt: draft.capturedAt || new Date().toISOString(),
          },
        };
      });
  const parsed = SyncPushSchema.safeParse({ deviceId: deviceId(), operations });
  if (!parsed.success) throw new Error(`Check the snag details: ${parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join('; ')}`);
  return parsed.data;
}

let previewProjectId: string | null = null;
export async function getLegacyData(refreshOnly = false): Promise<LegacyAppData> {
  if (isLocalDemo()) return demoData();
  const generation = ++dataRequestGeneration;
  const identity = api.sessionIdentity();
  api.beginDataRefresh();
  const [user, projects] = await Promise.all([api.me(), api.projects()]);
  if (cachedProjectUser !== user.id) { projectSnapshots.clear(); snagProjectIds.clear(); cachedProjectUser = user.id; }
  const stored = api.isViewingAs() ? previewProjectId : localStorage.getItem(ACTIVE_PROJECT_KEY);
  const requestedProject = projects.find((project) => project.id === stored) ?? projects[0];
  const toLoad = refreshOnly ? projects.filter((project) => project.id === requestedProject?.id || !projectSnapshots.has(project.id)) : projects;
  const bootstraps = await Promise.all(toLoad.map(async (project) => {
    try { return [project.id, await api.bootstrap(project.id)] as const; }
    catch (error) {
      if (error instanceof ApiError && [403, 404].includes(error.status)) return [project.id, null] as const;
      if (isNetworkFailure(error)) {
        if (project.id === requestedProject?.id) throw new NetworkError('This project has not finished saving on this device. Connect once to prepare it for offline use.');
        return [project.id, null] as const;
      }
      throw error; // A server failure must not masquerade as "no assigned projects".
    }
  }));
  if (identity !== api.sessionIdentity()) throw new ApiError("Account view changed. Please retry.", 409);
  const byId = new Map([...projectSnapshots].filter(([id]) => projects.some((project) => project.id === id)));
  for (const [id, snapshot] of bootstraps) { if (snapshot) byId.set(id, snapshot); else byId.delete(id); }
  if (generation === dataRequestGeneration) {
    projectSnapshots = byId; snagProjectIds.clear();
    for (const [id, snapshot] of byId) for (const snag of snapshot.snags) snagProjectIds.set(snag.id, id);
  }
  const availableProjects = projects.filter((project) => byId.has(project.id));
  const summaries = availableProjects.map((project) => projectSummary(project, byId.get(project.id)!));
  const activeProject = availableProjects.find((project) => project.id === stored) ?? availableProjects[0] ?? null;
  if (!api.isViewingAs() && activeProject && generation === dataRequestGeneration && localStorage.getItem(ACTIVE_PROJECT_KEY) === stored) localStorage.setItem(ACTIVE_PROJECT_KEY, activeProject.id);
  const active = activeProject ? byId.get(activeProject.id)! : null;
  // Cache the assigned files while connected, so opening the next snag does not
  // depend on having viewed that photo or drawing before losing the signal.
  if (!disconnected() && !api.usingOfflineData()) void api.warmProjects([...byId.values()]).catch(() => undefined);
  const contractors = active ? contractorRows(active, user) : [];
  const effectiveContractorCompanyId = active?.currentContractorCompanyId ?? user.contractorCompanyId;
  const currentContractor = user.role === "contractor"
    ? contractors.find((item) => item.id === effectiveContractorCompanyId) ?? { id: effectiveContractorCompanyId ?? user.id, company: user.name, trade: "Contractor", contactName: user.name, telephone: "", email: "", fullSnagListReadOnly: false, accountCount: 1, initials: initials(user.name), accessCode: "1 active account" }
    : null;
  const pending = (await queuedWork(api.workOwner(user.id))).filter(record => record.payload.version !== 3 && record.payload.projectId === activeProject?.id);
  const rows = active ? snagRows(active, contractors, user) : [];
  for (const { payload: item } of pending) {
    if (item.version === 3) continue;
    const snag = rows.find(value => value.id === item.snagId);
    if (!snag) continue;
    snag.pendingSync = true;
    if(item.pin && (snag.version ?? 0)<=item.pin.expectedVersion) { const drawing=active?.drawings.find(value=>value.id===item.pin!.drawingId); Object.assign(snag,{drawingId:item.pin.drawingId,drawingPage:item.pin.drawingPage,markupX:Math.round(item.pin.markupX*10000),markupY:Math.round(item.pin.markupY*10000),drawingName:drawing?.fileName ?? null,drawingContentType:drawing?.contentType ?? null,version:item.pin.expectedVersion+1}); }
    for (const operation of item.submission.operations) {
      if (operation.kind !== 'change_status') continue;
      // Never hide a newer server change behind an older queued status.
      if ((snag.version ?? 0) > operation.payload.expectedVersion) continue;
      snag.status = mappedStatus(operation.payload.status);
      snag.version = operation.payload.expectedVersion + 1;
      if (operation.payload.status === 'ready_to_inspect') snag.completionNote = operation.payload.note;
      else if (operation.payload.note) snag.managerNote = operation.payload.note;
    }
    for (const media of item.media) {
      const id = `pending:${item.ownerId}:${media.clientId}`;
      if (!mediaUrls.has(id)) mediaUrls.set(id, URL.createObjectURL(media.file));
      if (media.kind === 'completion') snag.completionPhotoId = id;
      else if (media.kind === 'issue') snag.issuePhotoId = id;
    }
  }
  return {
    mode: user.role === "contractor" ? "contractor" : "manager",
    currentContractor,
    project: activeProject && active ? projectSummary(activeProject, active) : blankProject(),
    projects: summaries,
    contractors,
    locations: active?.locations.map((item) => ({ id: item.id, name: item.name })) ?? [],
    drawings: active?.drawings.filter((item) => !item.superseded).map((item) => ({ id: item.id, fileName: item.fileName, contentType: item.contentType, size: item.sizeBytes })) ?? [],
    snags: rows,
  };
}

export function activateLegacyProject(projectId: string): void {
  if (api.isViewingAs()) { previewProjectId = projectId; return; }
  localStorage.setItem(ACTIVE_PROJECT_KEY, projectId);
  if (isLocalDemo()) demoActiveProjectId = projectId;
}

async function findSnag(snagId: string): Promise<{ snag: Snag; data: Bootstrap }> {
  const knownProjectId = snagProjectIds.get(snagId) ?? localStorage.getItem(ACTIVE_PROJECT_KEY);
  if (knownProjectId) {
    const data = await api.bootstrap(knownProjectId);
    const snag = data.snags.find((item) => item.id === snagId);
    if (snag) return { snag, data };
  }
  const projects = await api.projects();
  for (const project of projects) {
    const data = await api.bootstrap(project.id);
    const snag = data.snags.find((item) => item.id === snagId);
    if (snag) return { snag, data };
  }
  throw new Error("Snag unavailable");
}

function okay(data: unknown, status = 200): Response {
  return new Response(JSON.stringify(data), { status, headers: { "Content-Type": "application/json" } });
}

function bodyJson(init?: RequestInit): Record<string, any> {
  if (!init?.body || typeof init.body !== "string") return {};
  return JSON.parse(init.body) as Record<string, any>;
}

export async function legacyFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const value = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  const url = new URL(value, window.location.origin);
  if (isLocalDemo()) return demoFetch(url, init);
  try {
    if (url.pathname === "/api/state" && (!init.method || init.method === "GET")) return okay(await getLegacyData(url.searchParams.get("refresh") === "1"));

    if (url.pathname === "/api/manager/projects" && init.method === "POST") {
      const body = bodyJson(init);
      const project = await api.createProject({ name: String(body.name ?? ""), phase: String(body.phase ?? ""), address: String(body.address ?? ""), jobReference: String(body.jobReference ?? ""), managerAccessAll: Boolean(body.managerAccessAll), managerIds: Array.isArray(body.managerIds) ? body.managerIds.map(String) : [] });
      activateLegacyProject(project.id);
      return okay(project, 201);
    }
    if (url.pathname === "/api/manager/projects" && init.method === "PATCH") {
      const body = bodyJson(init);
      const id = String(body.id ?? "");
      if (body.activate) {
        activateLegacyProject(id);
        return okay({ id, active: true });
      }
      return okay(await api.updateProject(id, { name: String(body.name ?? ""), phase: String(body.phase ?? ""), address: String(body.address ?? ""), jobReference: String(body.jobReference ?? "") }));
    }
    if (url.pathname === "/api/manager/locations" && init.method === "POST") {
      const body = bodyJson(init);
      return okay(await api.createLocation(String(body.projectId), String(body.name)), 201);
    }
    if (url.pathname === "/api/manager/contractors" && init.method === "POST") {
      const body = bodyJson(init);
      return okay(await api.createContractor(String(body.projectId), {
        company: String(body.company), trade: String(body.trade),
        contactName: String(body.contactName ?? ""), telephone: String(body.telephone ?? ""), email: String(body.email ?? ""),
      }), 201);
    }
    if (url.pathname === "/api/manager/contractor-access" && init.method === "PATCH") {
      const body = bodyJson(init);
      return okay(await api.setContractorAccess(String(body.projectId), String(body.contractorCompanyId), Boolean(body.fullSnagListReadOnly)));
    }
    if (url.pathname === "/api/manager/contractors" && init.method === "DELETE") {
      const body = bodyJson(init);
      return okay(await api.removeContractor(String(body.projectId), String(body.contractorCompanyId)));
    }
    if (url.pathname === "/api/manager/contractor-invitations" && init.method === "POST") {
      const body = bodyJson(init);
      return okay(await api.inviteContractor(String(body.projectId), {
        contractorCompanyId: String(body.contractorCompanyId),
        kind: body.kind === "individual" ? "individual" : "company",
        ...(body.firstName ? { firstName: String(body.firstName) } : {}),
        ...(body.lastName ? { lastName: String(body.lastName) } : {}),
        ...(body.email ? { email: String(body.email) } : {}),
        ...(body.telephone ? { telephone: String(body.telephone) } : {}),
      }), 201);
    }
    if (url.pathname === "/api/manager/archive" && (!init.method || init.method === "GET")) {
      return okay({ snags: await api.archivedSnags(url.searchParams.get("projectId") || undefined) });
    }
    const permanentArchiveDeleteMatch = url.pathname.match(/^\/api\/manager\/archive\/snags\/([^/]+)$/);
    if (permanentArchiveDeleteMatch && init.method === "DELETE") {
      return okay(await api.permanentlyDeleteArchivedSnag(permanentArchiveDeleteMatch[1]!));
    }
    if (url.pathname === "/api/manager/drawings" && init.method === "POST") {
      const form = init.body as FormData;
      const file = form.get("drawing");
      if (!(file instanceof File)) throw new Error("Choose a drawing first");
      const projectId = String(form.get("projectId") ?? "");
      return okay(await api.uploadDrawing(projectId, file, file.name.replace(/\.[^.]+$/, ""), ""), 201);
    }
    if (url.pathname === "/api/audits" && init.method === "POST") {
      const body = bodyJson(init);
      const submission = await prepareAudit(String(body.projectId), body.snags as Array<Record<string, any>>);
      const result = await api.push(submission);
      const rejected = result.results.find((item) => !["applied", "already_applied"].includes(item.status));
      if (rejected) throw new Error(rejected.message);
      return okay({ snags: result.results.map((item) => ({ id: item.entityId })) }, 201);
    }

    const photoMatch = url.pathname.match(/^\/api\/snags\/([^/]+)\/photos$/);
    if (photoMatch && init.method === "POST") {
      const snagId = photoMatch[1]!;
      const { snag } = await findSnag(snagId);
      const form = init.body as FormData;
      const file = form.get("photo");
      if (!(file instanceof File)) throw new Error("Choose a photograph");
      const requested = url.searchParams.get("kind") ?? "issue";
      const kind = requested === "completion" ? "completion" : requested === "manager_close_off" ? "manager_close_off" : "issue";
      return okay(await api.uploadEvidence({ clientId: crypto.randomUUID(), projectId: snag.projectId, snagId, kind, capturedAt: new Date().toISOString(), file }), 201);
    }

    const recordStateMatch = url.pathname.match(/^\/api\/snags\/([^/]+)\/record-state$/);
    if (recordStateMatch && init.method === "PATCH") {
      const body = bodyJson(init);
      const state = String(body.recordState);
      if (!["live", "archived", "deleted"].includes(state)) throw new Error("Choose a valid archive action");
      return okay(await api.setSnagRecordState(recordStateMatch[1]!, state as Snag["recordState"]));
    }

    const snagMatch = url.pathname.match(/^\/api\/snags\/([^/]+)$/);
    if (snagMatch && init.method === "PATCH") {
      const snagId = snagMatch[1]!;
      const body = bodyJson(init);
      let { snag } = await findSnag(snagId);
      if (["contractorId", "priority", "locationId", "dueDate", "drawingId", "drawingPage", "markupX", "markupY"].some((key) => Object.prototype.hasOwnProperty.call(body, key))) {
        const nextPriority = ["low", "normal", "high", "urgent"].includes(String(body.priority ?? snag.priority))
          ? String(body.priority ?? snag.priority) as Snag["priority"]
          : snag.priority;
        snag = await api.updateSnag(snagId, {
          contractorCompanyId: Object.prototype.hasOwnProperty.call(body, "contractorId") ? (body.contractorId ? String(body.contractorId) : null) : snag.contractorCompanyId,
          priority: nextPriority,
          dueDate: Object.prototype.hasOwnProperty.call(body, "dueDate") ? (body.dueDate ? String(body.dueDate) : null) : snag.dueDate,
          locationId: Object.prototype.hasOwnProperty.call(body, "locationId") ? (body.locationId ? String(body.locationId) : null) : snag.locationId,
          drawingId: Object.prototype.hasOwnProperty.call(body, "drawingId") ? (body.drawingId ? String(body.drawingId) : null) : snag.drawingId,
          drawingPage: Object.prototype.hasOwnProperty.call(body, "drawingPage") ? (body.drawingPage == null ? null : Number(body.drawingPage)) : snag.drawingPage,
          markupX: Object.prototype.hasOwnProperty.call(body, "markupX") ? (body.markupX == null ? null : Number(body.markupX)) : snag.markupX,
          markupY: Object.prototype.hasOwnProperty.call(body, "markupY") ? (body.markupY == null ? null : Number(body.markupY)) : snag.markupY,
        });
      }
      if (body.status) {
        const note = String(body.completionNote ?? body.managerNote ?? body.rejectionNote ?? "");
        const result = await api.push({ deviceId: deviceId(), operations: [{
          kind: "change_status",
          operationId: crypto.randomUUID(),
          snagId,
          payload: { clientId: crypto.randomUUID(), status: serverStatus(String(body.status)), note, expectedVersion: snag.version, occurredAt: new Date().toISOString() },
        }] });
        const saved = result.results[0];
        if (!saved || !["applied", "already_applied"].includes(saved.status)) throw new Error(saved?.message ?? "Could not update snag");
      }
      return okay({ id: snagId });
    }

    return okay({ error: "Unsupported web-app request" }, 404);
  } catch (error) {
    return okay({ error: error instanceof Error ? error.message : "The change could not be saved" }, 400);
  }
}

async function mediaObjectUrl(key: string, fetchBlob: () => Promise<Blob>): Promise<string> {
  const existing = mediaUrls.get(key);
  if (existing) return existing;
  const pending = mediaRequests.get(key);
  if (pending) return pending;
  const generation = mediaGeneration;
  const request = fetchBlob().then((blob) => {
    if (generation !== mediaGeneration) throw new Error("Media session changed");
    const value = URL.createObjectURL(blob); mediaUrls.set(key, value); return value;
  }).finally(() => { if (generation === mediaGeneration) mediaRequests.delete(key); });
  mediaRequests.set(key, request);
  return request;
}

export async function evidenceObjectUrl(evidenceId: string): Promise<string> {
  if (isLocalDemo()) return demoPhoto(evidenceId);
  if (evidenceId.startsWith('pending:')) {
    const value = mediaUrls.get(evidenceId); if (value) return value;
    throw new Error('Saved evidence is loading. Reopen this snag.');
  }
  return mediaObjectUrl(`evidence:${evidenceId}`, () => api.evidenceBlob(evidenceId));
}

export async function drawingObjectUrl(drawingId: string): Promise<string> {
  if (isLocalDemo()) return "";
  return mediaObjectUrl(`drawing:${drawingId}`, () => api.drawingBlob(drawingId));
}

export function clearMediaUrls(): void {
  previewProjectId = null;
  dataRequestGeneration += 1; mediaGeneration += 1; mediaRequests = new Map();
  for (const value of mediaUrls.values()) if (value.startsWith("blob:")) URL.revokeObjectURL(value);
  mediaUrls = new Map();
  projectSnapshots.clear(); snagProjectIds.clear(); cachedProjectUser = "";
}

let demoActiveProjectId = "demo-project-1";
const demoProjects: LegacyProject[] = [
  { id: "demo-project-1", name: "Marks & Spencer's Croydon", phase: "RAAC works", address: "Croydon", jobReference: "MS-CROY", totalSnags: 6, openSnags: 3, reviewSnags: 2, closedSnags: 1 },
  { id: "demo-project-2", name: "Northgate School", phase: "Refurbishment · Phase 2", address: "County Durham", jobReference: "NGS-02", totalSnags: 7, openSnags: 1, reviewSnags: 1, closedSnags: 5 },
];
let demoContractors: LegacyContractor[] = [
  { id: "demo-c1", company: "Elacon", trade: "Electrical", contactName: "Ella Carter", telephone: "07700 900 112", email: "ella@elacon.example", fullSnagListReadOnly: false, accountCount: 2, initials: "EL", accessCode: "2 active accounts" },
  { id: "demo-c2", company: "Northline Joinery", trade: "Joinery", contactName: "", telephone: "", email: "", fullSnagListReadOnly: false, accountCount: 0, initials: "NJ", accessCode: "No accounts yet" },
  { id: "demo-c3", company: "Apex Doors", trade: "Doors", contactName: "Alex Price", telephone: "", email: "", fullSnagListReadOnly: false, accountCount: 1, initials: "AD", accessCode: "1 active account" },
];
let demoSnags: LegacySnag[] = [
  { id: "demo-s1", reference: "S-008", projectId: "demo-project-1", title: "Socket faceplate sitting proud", description: "Refit faceplate square and flush with the finished wall.", area: "First floor · Room 14", trade: "Electrical", priority: "normal", contractorId: "demo-c1", contractorCompany: "Elacon", contractorInitials: "EL", status: "ready_for_inspection", dueDate: "2026-08-22", completionNote: "Socket has been put back on the wall", managerNote: "", rejectionNote: "", createdAt: "2026-08-17", updatedAt: "2026-08-19", issuePhotoId: "demo-issue", completionPhotoId: null, drawingId: null, drawingName: null, drawingContentType: null, markupX: null, markupY: null, markupLabel: null, readOnly: false, recordState: "live", recordStateChangedAt: null },
  { id: "demo-s2", reference: "S-009", projectId: "demo-project-1", title: "Fire door closer not latching", description: "Adjust closer so the fire door fully latches.", area: "Ground floor · Corridor", trade: "Doors", priority: "urgent", contractorId: "demo-c3", contractorCompany: "Apex Doors", contractorInitials: "AD", status: "open", dueDate: "2026-08-18", completionNote: "", managerNote: "", rejectionNote: "", createdAt: "2026-08-18", updatedAt: "2026-08-18", issuePhotoId: null, completionPhotoId: null, drawingId: null, drawingName: null, drawingContentType: null, markupX: null, markupY: null, markupLabel: null, readOnly: false, recordState: "live", recordStateChangedAt: null },
  { id: "demo-s3", reference: "S-010", projectId: "demo-project-1", title: "Skirting joint needs resealing", description: "Joint has opened and needs a neat flexible seal.", area: "First floor · Room 12", trade: "Joinery", priority: "normal", contractorId: "demo-c2", contractorCompany: "Northline Joinery", contractorInitials: "NJ", status: "closed", dueDate: "2026-08-20", completionNote: "Sealed and cleaned", managerNote: "Checked on site", rejectionNote: "", createdAt: "2026-08-16", updatedAt: "2026-08-19", issuePhotoId: null, completionPhotoId: null, drawingId: null, drawingName: null, drawingContentType: null, markupX: null, markupY: null, markupLabel: null, readOnly: false, recordState: "live", recordStateChangedAt: null },
];

function demoData(): LegacyAppData {
  const project = demoProjects.find((item) => item.id === demoActiveProjectId) ?? demoProjects[0]!;
  return {
    mode: "manager",
    currentContractor: null,
    project,
    projects: demoProjects,
    contractors: demoContractors,
    locations: [{ id: "l1", name: "Ground floor · Corridor" }, { id: "l2", name: "First floor · Room 12" }, { id: "l3", name: "First floor · Room 14" }],
    drawings: [{ id: "d1", fileName: "0220 Croydon - RAAC DRAWING 1ST FL UP.pdf", contentType: "application/pdf", size: 348_000 }],
    snags: demoSnags.filter((item) => item.projectId === project.id && item.recordState === "live"),
  };
}

function demoPhoto(id: string): string {
  void id;
  return "";
}

async function demoFetch(url: URL, init: RequestInit): Promise<Response> {
  if (url.pathname === "/api/state") return okay(demoData());
  if (url.pathname === "/api/manager/projects" && init.method === "PATCH") {
    const body = bodyJson(init); activateLegacyProject(String(body.id)); return okay({ active: true });
  }
  if (url.pathname === "/api/manager/projects" && init.method === "POST") {
    const body = bodyJson(init); const project = { id: `demo-project-${Date.now()}`, name: String(body.name), phase: String(body.phase ?? ""), address: String(body.address ?? ""), jobReference: String(body.jobReference ?? ""), totalSnags: 0, openSnags: 0, reviewSnags: 0, closedSnags: 0 }; demoProjects.unshift(project); activateLegacyProject(project.id); return okay(project, 201);
  }
  if (url.pathname === "/api/audits" && init.method === "POST") {
    const body = bodyJson(init); const created = (body.snags as Array<Record<string, any>>).map((item, index) => { const id = `demo-new-${Date.now()}-${index}`; demoSnags.push({ id, reference: `S-${String(demoSnags.length + 11).padStart(3, "0")}`, projectId: String(body.projectId), title: String(item.title), description: String(item.description ?? ""), area: String(item.area ?? "Area not recorded"), trade: String(item.trade ?? "General"), priority: String(item.priority ?? "normal"), contractorId: item.contractorId ? String(item.contractorId) : null, contractorCompany: demoContractors.find((row) => row.id === item.contractorId)?.company ?? null, contractorInitials: demoContractors.find((row) => row.id === item.contractorId)?.initials ?? null, status: "open", dueDate: item.dueDate ? String(item.dueDate) : null, completionNote: "", managerNote: "", rejectionNote: "", createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), issuePhotoId: null, completionPhotoId: null, drawingId: null, drawingName: null, drawingContentType: null, markupX: null, markupY: null, markupLabel: null, readOnly: false, recordState: "live", recordStateChangedAt: null }); return { id }; }); return okay({ snags: created }, 201);
  }
  if (url.pathname === "/api/manager/contractors" && init.method === "POST") {
    const body = bodyJson(init);
    const contractor: LegacyContractor = {
      id: `demo-c-${Date.now()}`, company: String(body.company), trade: String(body.trade),
      contactName: String(body.contactName ?? ""), telephone: String(body.telephone ?? ""), email: String(body.email ?? ""), fullSnagListReadOnly: false,
      accountCount: 0, initials: initials(String(body.company)), accessCode: "No accounts yet",
    };
    demoContractors = [...demoContractors, contractor];
    return okay(contractor, 201);
  }
  if (url.pathname === "/api/manager/contractor-invitations" && init.method === "POST") {
    return okay({ token: `demo-invitation-${crypto.randomUUID()}-${crypto.randomUUID()}`, expiresAt: new Date(Date.now() + 7 * 86400000).toISOString() }, 201);
  }
  if (url.pathname === "/api/manager/contractor-access" && init.method === "PATCH") {
    const body = bodyJson(init);
    demoContractors = demoContractors.map((item) => item.id === body.contractorCompanyId ? { ...item, fullSnagListReadOnly: Boolean(body.fullSnagListReadOnly) } : item);
    return okay({ id: body.contractorCompanyId, fullSnagListReadOnly: Boolean(body.fullSnagListReadOnly) });
  }
  if (url.pathname === "/api/manager/archive" && (!init.method || init.method === "GET")) {
    const projectId = url.searchParams.get("projectId");
    return okay({ snags: demoSnags.filter((item) => item.recordState !== "live" && (!projectId || item.projectId === projectId)) });
  }
  const permanentArchiveDeleteMatch = url.pathname.match(/^\/api\/manager\/archive\/snags\/([^/]+)$/);
  if (permanentArchiveDeleteMatch && init.method === "DELETE") {
    const selected = demoSnags.find((item) => item.id === permanentArchiveDeleteMatch[1]);
    if (!selected || selected.recordState === "live") return okay({ error: "Move this snag into the archive before permanently deleting it" }, 409);
    demoSnags = demoSnags.filter((item) => item.id !== permanentArchiveDeleteMatch[1]);
    return okay({ deleted: true });
  }
  const recordStateMatch = url.pathname.match(/^\/api\/snags\/([^/]+)\/record-state$/);
  if (recordStateMatch && init.method === "PATCH") {
    const body = bodyJson(init);
    demoSnags = demoSnags.map((item) => item.id === recordStateMatch[1] ? { ...item, recordState: body.recordState, recordStateChangedAt: new Date().toISOString(), updatedAt: new Date().toISOString() } : item);
    return okay(demoSnags.find((item) => item.id === recordStateMatch[1]));
  }
  const snagMatch = url.pathname.match(/^\/api\/snags\/([^/]+)$/);
  if (snagMatch && init.method === "PATCH") { const body = bodyJson(init); demoSnags = demoSnags.map((item) => item.id === snagMatch[1] ? { ...item, contractorId: body.contractorId === undefined ? item.contractorId : body.contractorId, priority: body.priority ?? item.priority, status: body.status ?? item.status, completionNote: body.completionNote ?? item.completionNote, managerNote: body.managerNote ?? item.managerNote, rejectionNote: body.rejectionNote ?? item.rejectionNote, updatedAt: new Date().toISOString() } : item); return okay({ id: snagMatch[1] }); }
  if (url.pathname.includes("/photos") || url.pathname.includes("/locations") || url.pathname.includes("/contractors") || url.pathname.includes("/drawings")) return okay({ id: `demo-${Date.now()}`, name: "Demo item" }, 201);
  return okay({ error: "Unsupported demo request" }, 404);
}
